import React, { useCallback, useEffect, useState } from 'react';
import { useIndicator } from 'shared/providers/IndicatorProvider';
import { useFeatureFlags } from 'shared/providers/FeatureFlags/useFeatureFlags';
import styled from 'styled-components';
import {
  faCircleExclamation,
  faPeopleGroup,
  faTrash,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Tooltip from '@mui/material/Tooltip';
import TrueImpactModal, {
  modalHeightAutoStyles,
  modalRadiusStyles,
} from 'shared/design-system/components/TrueImpactModal';
import TrueImpactConfirmationDialog from 'shared/design-system/components/TrueImpactConfirmationDialog';
import TrueImpactIconText from 'shared/design-system/components/TrueImpactIconText';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import {
  getBeneficiaryQuestionText,
  renderDirectBeneficiaryText,
  renderIndirectBeneficiaryText,
} from '../../../../../ReportBuilder/Components/SectionMultiRadio';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import Radiobutton from 'shared/design-system/components/radio/Radiobutton';
import { capitalizeFirstLetter } from 'shared/utils/stringUtils';
import { useClient } from 'shared/apollo';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import {
  clearImpactReportAnswers,
  ClearImpactReportAnswersResponse,
} from '../../../api';

const StyledDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: ${SPACERS.medium};
  margin-bottom: ${SPACERS.medium};
  .action-chip {
    display: flex;
    align-items: center;
    background-color: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.grayLight};
    border-radius: 999px;
    padding: 0 ${SPACERS.small};
    transition: box-shadow 0.2s ease, border-color 0.2s ease,
      transform 0.2s ease;
    &:hover {
      border-color: ${COLORS.ctaPrimarySubdued};
      box-shadow: 0 2px 8px rgba(0, 115, 163, 0.12);
      transform: translateY(-1px);
    }
  }
  .action-chip-subtle {
    &:hover {
      border-color: ${COLORS.grayBase};
      box-shadow: 0 2px 8px rgba(68, 87, 105, 0.1);
    }
  }
`;

const StyledConfirmationBody = styled.div`
  text-align: center;
  .confirmation-icon-badge {
    width: 64px;
    height: 64px;
    margin: 0 auto ${SPACERS.medium};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background-color: ${COLORS.criticalBackground};
    svg {
      font-size: 28px;
      color: ${COLORS.critical};
    }
  }
  .confirmation-title {
    margin-bottom: ${SPACERS.small};
  }
  .confirmation-warning {
    display: flex;
    align-items: flex-start;
    gap: ${SPACERS.small};
    text-align: left;
    margin: 0 auto;
    max-width: 480px;
    padding: ${SPACERS.small} ${SPACERS.medium};
    background-color: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.grayLight};
    border-left: 3px solid ${COLORS.critical};
    border-radius: ${SPACERS.tiny};
    .confirmation-warning-icon {
      margin-top: 2px;
      color: ${COLORS.critical};
      flex-shrink: 0;
    }
  }
  .confirmation-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${SPACERS.tiny};
    margin: ${SPACERS.medium} auto 0;
    max-width: 480px;
    padding: ${SPACERS.small} ${SPACERS.medium};
    background-color: ${COLORS.criticalBackground};
    border: 1px solid ${COLORS.criticalSubdued};
    border-radius: ${SPACERS.tiny};
  }
`;

const StyledModelDiv = styled.div`
  .beneficiary-modal-header {
    padding: ${SPACERS.medium} ${SPACERS.large};
    background-color: ${COLORS.whiteBase};
    border-bottom: 1px solid ${COLORS.grayLight};
    .beneficiary-modal-heading {
      margin-bottom: ${SPACERS.tiny};
    }
  }
  .beneficiary-modal-body {
    padding: ${SPACERS.medium} ${SPACERS.large} ${SPACERS.large}
      ${SPACERS.large};
  }
  .beneficiary-model {
    margin-bottom: ${SPACERS.large};
    .beneficiary-model-label {
      margin-bottom: ${SPACERS.tiny};
    }
  }
  .intermediary-model {
    margin-top: ${SPACERS.medium};
    padding: ${SPACERS.medium};
    background-color: ${COLORS.ctaPrimaryBackgroundLighter};
    border: 1px solid ${COLORS.ctaPrimaryBackground};
    border-radius: ${SPACERS.tiny};
    .intermediary-model-label {
      margin-bottom: ${SPACERS.tiny};
    }
  }
  .intermediary-radio {
    margin-top: ${SPACERS.medium};
    .intermediary-radio-option {
      border: 1px solid ${COLORS.grayLight};
    }
  }
  .beneficiary-radio {
    width: 100%;
    margin-bottom: ${SPACERS.small};
    background-color: ${COLORS.whiteBase};
    border: 1px solid ${COLORS.grayLight};
    border-radius: ${SPACERS.tiny};
    gap: ${SPACERS.small};
    padding: ${SPACERS.small} ${SPACERS.medium};
    cursor: pointer;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    &:hover {
      border-color: ${COLORS.ctaPrimarySubdued};
      box-shadow: 0 2px 8px rgba(0, 115, 163, 0.1);
    }
    .beneficiary-radio-option {
      margin-right: 0;
      min-width: 20px;
    }
    .beneficiary-radio-text {
      text-align: left;
      .beneficiary-model-text {
        margin-bottom: 0 !important;
      }
    }
  }
`;

const BeneficiaryModelUpdates: React.FC = () => {
  const [show, setShow] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const needToRestartImpactModel = useFeatureFlags('needToRestartImpactModel');
  const {
    beneficiaryName,
    intermediaries,
    savingState,
    reportId,
    handleUpdateBeneficiaryTerms,
  } = useIndicator();
  const client = useClient();
  const [deleteState, makeDeleteRequest, setDeleteState] = useGraphQLRequest<
    ClearImpactReportAnswersResponse
  >(undefined, true);
  const [showIntermediary, setShowIntermediary] = useState<boolean>(
    Boolean(intermediaries),
  );
  const [beneficiaryValue, setBeneficiaryValue] = useState<string>(
    beneficiaryName || '',
  );
  const [intermediaryValue, setIntermediaryValue] = useState<string>(
    intermediaries || '',
  );

  const handleOpen = useCallback(() => setShow(true), []);
  const handleClose = useCallback(() => setShow(false), []);

  const handleSubmit = useCallback(() => {
    const newBeneficiaryValue = capitalizeFirstLetter(beneficiaryValue.trim());
    const newIntermediaryValue = capitalizeFirstLetter(
      intermediaryValue.trim(),
    );
    handleUpdateBeneficiaryTerms(newBeneficiaryValue, newIntermediaryValue);
    setBeneficiaryValue(newBeneficiaryValue);
    setIntermediaryValue(newIntermediaryValue);
  }, [
    beneficiaryValue,
    intermediaryValue,
    setBeneficiaryValue,
    setIntermediaryValue,
    handleUpdateBeneficiaryTerms,
  ]);

  useEffect(() => {
    if (!showIntermediary) {
      setIntermediaryValue('');
    }
  }, [showIntermediary]);

  useEffect(() => {
    if (savingState.type === 'REQUEST_SUCCESS') {
      handleClose();
    }
  }, [savingState.type, handleClose]);

  const handleOpenDeleteConfirm = useCallback(() => {
    setDeleteState({ type: 'REQUEST_INIT', data: null });
    setShowDeleteConfirm(true);
  }, [setDeleteState]);

  const handleCloseDeleteConfirm = useCallback(() => {
    if (deleteState.type === 'REQUEST_START') return;
    setShowDeleteConfirm(false);
  }, [deleteState.type]);

  const handleConfirmDelete = useCallback(() => {
    if (!client || !reportId) return;
    makeDeleteRequest(clearImpactReportAnswers(client, reportId, false));
  }, [client, reportId, makeDeleteRequest]);

  useEffect(() => {
    if (
      deleteState.type === 'REQUEST_SUCCESS' &&
      deleteState.data?.clearAnswersBySections?.success
    ) {
      setShowDeleteConfirm(false);
      window.location.reload();
    }
  }, [deleteState]);

  const deleteErrorMessage =
    deleteState.type === 'REQUEST_ERROR'
      ? deleteState.error?.response?.data?.message ||
        'Something went wrong. Please try again.'
      : deleteState.type === 'REQUEST_SUCCESS' &&
        !deleteState.data?.clearAnswersBySections?.success
      ? 'Something went wrong. Please try again.'
      : undefined;

  const isDeleting = deleteState.type === 'REQUEST_START';

  return (
    <>
      <StyledDiv>
        <div className="action-chip">
          <TrueImpactIconText
            icon={faPeopleGroup}
            onClick={handleOpen}
            label="Update beneficiary terms"
            hoverColor="ctaPrimaryHover"
          />
        </div>
        {needToRestartImpactModel && (
          <Tooltip
            arrow={true}
            title={'Clicking the \'Delete Model\' button allows you to delete your current model and start a new one from scratch. Note ' +
              'that you will have to answer all questions in the Impact Report Builder and Impact Report Details again.'}
          >
            <div className="action-chip action-chip-subtle">
              <TrueImpactIconText
                icon={faTrash}
                onClick={handleOpenDeleteConfirm}
                label="Need to start over with a new model?"
                color="grayDark"
                hoverColor="grayDarkest"
              />
            </div>
          </Tooltip>
        )}
      </StyledDiv>
      <TrueImpactConfirmationDialog
        open={showDeleteConfirm}
        handleClose={handleCloseDeleteConfirm}
        padding={SPACERS.extraLarge}
        next={{
          onClick: handleConfirmDelete,
          text: 'Yes, delete model',
          isLoading: isDeleting,
          disabled: isDeleting,
        }}
        previous={{
          onClick: handleCloseDeleteConfirm,
          text: 'No, keep current model',
          disabled: isDeleting,
        }}
      >
        <StyledConfirmationBody>
          <div className="confirmation-icon-badge">
            <FontAwesomeIcon icon={faTrash} />
          </div>
          <TrueImpactText
            variant="body-large-bold"
            className="confirmation-title"
          >
            Are you sure you want to delete your model and build a new one from
            scratch?
          </TrueImpactText>
          <div className="confirmation-warning">
            <FontAwesomeIcon
              icon={faCircleExclamation}
              className="confirmation-warning-icon"
            />
            <TrueImpactText variant="body-small" color="grayDark">
              This will permanently remove your current model. You will need to
              answer all questions in the Impact Report Builder and Impact
              Report Details again.
            </TrueImpactText>
          </div>
          {deleteErrorMessage && (
            <div className="confirmation-error">
              <FontAwesomeIcon
                icon={faCircleExclamation}
                color={COLORS.critical}
              />
              <TrueImpactText variant="body-small" color="critical">
                {deleteErrorMessage}
              </TrueImpactText>
            </div>
          )}
        </StyledConfirmationBody>
      </TrueImpactConfirmationDialog>
      <TrueImpactModal
        open={show}
        maxWidth="800px"
        hideHeader={true}
        addStyles={modalHeightAutoStyles + modalRadiusStyles}
        onClose={handleClose}
        next={{
          onClick: handleSubmit,
          disabled:
            !beneficiaryValue || (showIntermediary && !intermediaryValue),
          isLoading: savingState.type === 'REQUEST_START',
          text: 'Update beneficiary terms',
        }}
        previous={{
          onClick: handleClose,
          text: 'Cancel',
        }}
      >
        <StyledModelDiv>
          <div className="beneficiary-modal-header">
            <TrueImpactText
              variant="body-large-bold"
              className="beneficiary-modal-heading"
            >
              Update beneficiary terms
            </TrueImpactText>
            <TrueImpactText
              variant="body-small"
              color="grayDark"
              className="beneficiary-modal-subheading"
            >
              These terms are used throughout your report to describe who your
              program serves.
            </TrueImpactText>
          </div>
          <div className="beneficiary-modal-body">
            <div className="beneficiary-model">
              <TrueImpactText
                variant="body-bold"
                className="beneficiary-model-label"
              >
                Who or what does your program primarily help?
              </TrueImpactText>
              <TrueImpactTextfield
                value={beneficiaryValue}
                placeholder="Enter beneficiary term"
                onChange={setBeneficiaryValue}
              />
            </div>
            <Radiobutton
              check={showIntermediary === false}
              onClick={() => setShowIntermediary(false)}
              text={renderDirectBeneficiaryText(
                getBeneficiaryQuestionText(
                  beneficiaryValue || 'Beneficiaries',
                  0,
                ),
              )}
              className="beneficiary-radio"
              textClassName="beneficiary-radio-text"
              radioClassName="beneficiary-radio-option"
            />
            <Radiobutton
              check={showIntermediary === true}
              onClick={() => setShowIntermediary(true)}
              text={renderIndirectBeneficiaryText(
                getBeneficiaryQuestionText(
                  beneficiaryValue || 'Beneficiaries',
                  1,
                ),
              )}
              className="beneficiary-radio"
              textClassName="beneficiary-radio-text"
              radioClassName="beneficiary-radio-option"
            />
            {/* <TrueImpactRadio<boolean>
            selectedOption={showIntermediary}
            options={[false, true]}
            compare={(a, b) => a === b}
            className="intermediary-radio"
            radioClassName="intermediary-radio-option"
            getId={a => (a ? 'true' : 'false')}
            getLabel={a =>
              a === false
                ? renderDirectBeneficiaryText(
                    getBeneficiaryQuestionText(beneficiaryName, 0),
                  )
                : renderIndirectBeneficiaryText(
                    getBeneficiaryQuestionText(beneficiaryName, 1),
                  )
            }
            onChange={setShowIntermediary}
          /> */}
            {showIntermediary && (
              <div className="intermediary-model">
                <TrueImpactText
                  variant="body-bold"
                  className="intermediary-model-label"
                >
                  What term do you use for the intermediaries you directly
                  serve?
                </TrueImpactText>
                <TrueImpactTextfield
                  value={intermediaryValue}
                  placeholder="Enter intermediaries term"
                  onChange={setIntermediaryValue}
                />
              </div>
            )}
          </div>
        </StyledModelDiv>
      </TrueImpactModal>
    </>
  );
};

export default BeneficiaryModelUpdates;
